import { Coordinate } from "game-common/models"

export const BUILDING_SHAPES = ["rectangle", "ellipse", "line"] as const
export type BuildingShape = typeof BUILDING_SHAPES[number]
export type BuildingLineAxis = "horizontal" | "vertical"
export interface BuildingAreaEdges { x?: number; y?: number }

/** Snap only the moving sides to parallel wall runs intersecting this rectangle. */
export const snapBuildingArea = (anchor: Coordinate, pointer: Coordinate, wallAt: (point: Coordinate) => boolean,
    width: number, height: number, previous: BuildingAreaEdges = {}, bypass = false):
    { endpoint: Coordinate; edges: BuildingAreaEdges } => {
    const edges: BuildingAreaEdges = {}
    if (bypass) return { endpoint: { ...pointer }, edges }
    const choose = (axis: "x" | "y", spanEnd: number) => {
        if (Math.abs(pointer[axis] - anchor[axis]) < 2) return undefined
        const other = axis === "x" ? "y" : "x"
        const limit = axis === "x" ? width : height, spanLimit = axis === "x" ? height : width
        const min = Math.max(0, Math.min(anchor[other], spanEnd)), max = Math.min(spanLimit - 1, Math.max(anchor[other], spanEnd))
        const eligible = (edge: number) => {
            if (edge < 0 || edge >= limit || Math.sign(edge - anchor[axis]) !== Math.sign(pointer[axis] - anchor[axis])) return false
            for (let i = min; i <= max; i++) {
                const point = axis === "x" ? { x: edge, y: i } : { x: i, y: edge }
                if (wallAt(point) && (wallAt(axis === "x" ? { x: edge, y: i - 1 } : { x: i - 1, y: edge }) ||
                    wallAt(axis === "x" ? { x: edge, y: i + 1 } : { x: i + 1, y: edge }))) return true
            }
            return false
        }
        const held = previous[axis]
        if (held !== undefined && Math.abs(held - pointer[axis]) <= 2 && eligible(held)) return held
        for (const edge of [pointer[axis], pointer[axis] - 1, pointer[axis] + 1]) if (eligible(edge)) return edge
        return undefined
    }
    edges.x = choose("x", pointer.y)
    edges.y = choose("y", edges.x ?? pointer.x)
    return { endpoint: { x: edges.x ?? pointer.x, y: edges.y ?? pointer.y }, edges }
}

/** A small acquisition zone and wider release zone keep a near-straight drag steady. */
export const snapBuildingLine = (anchor: Coordinate, pointer: Coordinate, axis?: BuildingLineAxis,
    bypass = false): { endpoint: Coordinate; axis?: BuildingLineAxis } => {
    if (bypass) return { endpoint: { ...pointer } }
    const dx = Math.abs(pointer.x - anchor.x), dy = Math.abs(pointer.y - anchor.y)
    if (Math.max(dx, dy) < 2) return { endpoint: { ...pointer } }
    if (axis === "horizontal" && dy > Math.max(2, dx * 0.65)) axis = undefined
    if (axis === "vertical" && dx > Math.max(2, dy * 0.65)) axis = undefined
    if (!axis) {
        if (dx >= 2 && dy <= Math.max(1, dx * 0.35)) axis = "horizontal"
        else if (dy >= 2 && dx <= Math.max(1, dy * 0.35)) axis = "vertical"
    }
    return { axis, endpoint: axis === "horizontal" ? { x: pointer.x, y: anchor.y } :
        axis === "vertical" ? { x: anchor.x, y: pointer.y } : { ...pointer } }
}

/** Attract only to nearby ends/corners, with a wider hold zone and no competition with axis lock. */
export const snapWallConnection = (anchor: Coordinate, pointer: Coordinate, axis: BuildingLineAxis | undefined,
    wallAt: (point: Coordinate) => boolean, previous?: Coordinate, bypass = false, diagonal = false): Coordinate | undefined => {
    if (bypass) return
    const distance = (point: Coordinate) => diagonal ? Math.max(Math.abs(point.x - pointer.x), Math.abs(point.y - pointer.y)) :
        Math.abs(point.x - pointer.x) + Math.abs(point.y - pointer.y)
    const eligible = (point: Coordinate) => {
        if (point.x === anchor.x && point.y === anchor.y) return false
        if (axis === "horizontal" && point.y !== anchor.y || axis === "vertical" && point.x !== anchor.x) return false
        if (!wallAt(point)) return false
        const north = wallAt({ x: point.x, y: point.y - 1 }), south = wallAt({ x: point.x, y: point.y + 1 })
        const west = wallAt({ x: point.x - 1, y: point.y }), east = wallAt({ x: point.x + 1, y: point.y })
        const neighbours = Number(north) + Number(south) + Number(west) + Number(east)
        return neighbours <= 1 || neighbours === 2 && !(north && south || west && east)
    }
    if (previous && distance(previous) <= 2 && eligible(previous)) return { ...previous }
    let best: Coordinate | undefined
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const point = { x: pointer.x + dx, y: pointer.y + dy }
        if (distance(point) > 1 || best && distance(point) >= distance(best) || !eligible(point)) continue
        best = point
    }
    return best
}

export const buildingShapeCells = (anchor: Coordinate, corner: Coordinate, shape: BuildingShape, hollow: boolean,
    width: number, height: number, limit = 256): Coordinate[] => {
    const result: Coordinate[] = []
    const seen = new Set<string>()
    const add = (x: number, y: number) => {
        const key = `${x},${y}`
        if (result.length >= limit || x < 0 || y < 0 || x >= width || y >= height || seen.has(key)) return
        seen.add(key)
        result.push({ x, y })
    }
    const dx = corner.x - anchor.x, dy = corner.y - anchor.y
    const connect = (from: Coordinate, to: Coordinate) => {
        const dx = to.x - from.x, dy = to.y - from.y
        const nx = Math.abs(dx), ny = Math.abs(dy)
        let x = from.x, y = from.y, ix = 0, iy = 0
        add(x, y)
        while ((ix < nx || iy < ny) && result.length < limit) {
            if (ix < nx && (iy >= ny || (1 + 2 * ix) * ny <= (1 + 2 * iy) * nx)) {
                x += Math.sign(dx); ix++
            } else { y += Math.sign(dy); iy++ }
            add(x, y)
        }
    }
    if (shape === "line") {
        connect(anchor, corner)
        return result
    }
    const minX = Math.max(0, Math.min(anchor.x, corner.x)), maxX = Math.min(width - 1, Math.max(anchor.x, corner.x))
    const minY = Math.max(0, Math.min(anchor.y, corner.y)), maxY = Math.min(height - 1, Math.max(anchor.y, corner.y))
    const cx = (anchor.x + corner.x) / 2, cy = (anchor.y + corner.y) / 2
    const rx = Math.abs(dx) / 2, ry = Math.abs(dy) / 2
    if (shape === "ellipse" && hollow && rx > 0 && ry > 0) {
        const steps = Math.max(16, Math.ceil(Math.max(rx, ry) * 16))
        const pointAt = (step: number) => ({ x: Math.round(cx + rx * Math.cos(step * Math.PI * 2 / steps)),
            y: Math.round(cy + ry * Math.sin(step * Math.PI * 2 / steps)) })
        let previous = pointAt(0)
        for (let step = 1; step <= steps && result.length < limit; step++) {
            const point = pointAt(step)
            connect(previous, point)
            previous = point
        }
        return result
    }
    const inside = (x: number, y: number) => {
        if (x < minX || x > maxX || y < minY || y > maxY) return false
        if (rx === 0 || ry === 0) return true
        return ((x - cx) / (rx + 0.5)) ** 2 + ((y - cy) / (ry + 0.5)) ** 2 <= 1 + 1e-9
    }
    const stepX = dx >= 0 ? 1 : -1, stepY = dy >= 0 ? 1 : -1
    for (let y = stepY > 0 ? minY : maxY; y >= minY && y <= maxY; y += stepY) {
        for (let x = stepX > 0 ? minX : maxX; x >= minX && x <= maxX; x += stepX) {
            if (shape === "ellipse") {
                if (!inside(x, y)) continue
                if (hollow && inside(x - 1, y) && inside(x + 1, y) && inside(x, y - 1) && inside(x, y + 1)) continue
            } else if (hollow && x !== minX && x !== maxX && y !== minY && y !== maxY) continue
            add(x, y)
            if (result.length >= limit) return result
        }
    }
    return result
}
